// Neighbor-aware terrain variants.
//
// Direct ports of WinBolo's screencalc.c. We mirror five of the seven
// per-terrain functions:
//
//   screenCalcDeepSea  -> DEEP_SEA + 4 corners + 4 sides
//                         (BOAT counts as RIVER for comparison).
//   screenCalcRiver    -> 15 variants: SURROUND / 4 ENDs / 4 CORNs /
//                         2 SIDEs / 4 ONESIDEs / SOLID.
//                         (DEEP_SEA + BOAT count as RIVER; ROAD counts
//                         as river-connected so a road bridge completes
//                         the bank.)
//   screenCalcForest   -> 4 corners + 4 cardinal edges + SINGLE
//                         (cardinal-only).
//   screenCalcBuilding -> 46 variants covering corners, sides,
//                         T-junctions, side-corners, twists, "most-of"
//                         tiles, the cross, and 8 end forms.
//                         (HALFBUILDING counts as BUILDING for
//                         comparison so half-built walls thread into
//                         finished walls correctly.)
//   screenCalcRoad     -> ~30 variants including 8 corners, 4 T's,
//                         4 sides, the cross, ENDs, and 11 ROAD_WATER
//                         hybrids that handle road-meets-river bends.
//                         (DEEP_SEA + BOAT count as RIVER.)
//
// screenCalcCrater in the original has an early `return CRATER` before
// any variant logic — effectively dead code — so craters always render
// as a single tile. We do the same.
//
// All coords match winbolo/src/gui/tiles.h verbatim.

import { Terrain } from "@webolo/shared";
import { BOAT_ORIENTATION } from "./tilesheet.js";

export interface Neighbors {
  aboveLeft: Terrain;
  above: Terrain;
  aboveRight: Terrain;
  left: Terrain;
  right: Terrain;
  belowLeft: Terrain;
  below: Terrain;
  belowRight: Terrain;
}

// (x, y) source rect in tiles.bmp.
type Pos = readonly [number, number];

export const DEEP_SEA_VARIANTS = {
  SOLID: [0, 16],
  CORN1: [160, 48],
  CORN2: [176, 48],
  CORN3: [192, 48],
  CORN4: [208, 48],
  SIDE1: [224, 48],
  SIDE2: [240, 48],
  SIDE3: [256, 48],
  SIDE4: [272, 48],
} as const;

export const RIVER_VARIANTS = {
  SOLID:    [16, 16],
  SURROUND: [480, 32],
  END1:     [416, 32],
  END2:     [432, 32],
  END3:     [448, 32],
  END4:     [464, 32],
  CORN1:    [96, 48],
  CORN2:    [112, 48],
  CORN3:    [128, 48],
  CORN4:    [144, 48],
  SIDE1:    [0, 48],
  SIDE2:    [16, 48],
  ONESIDE1: [32, 48],
  ONESIDE2: [48, 48],
  ONESIDE3: [64, 48],
  ONESIDE4: [80, 48],
} as const;

export const FOREST_VARIANTS = {
  SINGLE: [128, 144],
  BR:     [144, 144],
  BL:     [160, 144],
  AL:     [176, 144],
  AR:     [192, 144],
  ABOVE:  [256, 144],
  BELOW:  [240, 144],
  LEFT:   [224, 144],
  RIGHT:  [208, 144],
} as const;

export const BUILD_VARIANTS = {
  SOLID:     [272, 16],
  CROSS:     [480, 16],
  HORZ:      [176, 16],
  VERT:      [192, 16],
  HORZEND1:  [208, 16],
  HORZEND2:  [224, 16],
  VERTEND1:  [240, 16],
  VERTEND2:  [256, 16],
  SINGLE:    [96, 16],
  CORNER1:   [288, 16],
  CORNER2:   [304, 16],
  CORNER3:   [320, 16],
  CORNER4:   [336, 16],
  L1:        [352, 16],
  L2:        [368, 16],
  L3:        [384, 16],
  L4:        [400, 16],
  T1:        [416, 16],
  T2:        [432, 16],
  T3:        [448, 16],
  T4:        [464, 16],
  SIDE1:     [0, 32],
  SIDE2:     [16, 32],
  SIDE3:     [32, 32],
  SIDE4:     [48, 32],
  SIDECORN1: [64, 32],
  SIDECORN2: [80, 32],
  SIDECORN3: [96, 32],
  SIDECORN4: [112, 32],
  SIDECORN5: [128, 32],
  SIDECORN6: [144, 32],
  SIDECORN7: [160, 32],
  SIDECORN8: [176, 32],
  SIDECORN9: [192, 32],
  SIDECORN10: [208, 32],
  SIDECORN11: [224, 32],
  SIDECORN12: [240, 32],
  SIDECORN13: [256, 32],
  SIDECORN14: [272, 32],
  SIDECORN15: [288, 32],
  SIDECORN16: [304, 32],
  TWIST1:    [320, 32],
  TWIST2:    [336, 32],
  MOST1:     [352, 32],
  MOST2:     [368, 32],
  MOST3:     [384, 32],
  MOST4:     [400, 32],
} as const;

export const ROAD_VARIANTS = {
  SOLID:    [160, 0],
  HORZ:     [0, 0],
  VERT:     [16, 0],
  CROSS:    [176, 0],
  T1:       [192, 0],
  T2:       [208, 0],
  T3:       [224, 0],
  T4:       [240, 0],
  CORNER1:  [32, 0],
  CORNER2:  [48, 0],
  CORNER3:  [64, 0],
  CORNER4:  [80, 0],
  CORNER5:  [96, 0],
  CORNER6:  [112, 0],
  CORNER7:  [128, 0],
  CORNER8:  [144, 0],
  SIDE1:    [432, 0],
  SIDE2:    [448, 0],
  SIDE3:    [464, 0],
  SIDE4:    [480, 0],
  WATER1:   [256, 0],
  WATER2:   [272, 0],
  WATER3:   [288, 0],
  WATER4:   [304, 0],
  WATER5:   [320, 0],
  WATER6:   [336, 0],
  WATER7:   [352, 0],
  WATER8:   [368, 0],
  WATER9:   [384, 0],
  WATER10:  [400, 0],
  WATER11:  [416, 0],
} as const;

// ---- helpers --------------------------------------------------------

// "river-connected" treats ROAD the same as RIVER for connection logic
// (a road bridge counts as completing the river path).
function isRiverConn(t: Terrain): boolean {
  return t === Terrain.River || t === Terrain.Road;
}

// "is forest for variant purposes". Pillbox / Base tiles authored on top
// of forest don't connect; we treat them as non-forest neighbors.
function isForest(t: Terrain): boolean {
  return t === Terrain.Forest;
}

// "is building for variant purposes". HalfBuiltWall counts as a finished
// wall so a partially-built wall threads correctly into surrounding
// finished walls (mirrors screenCalcBuilding's HALFBUILDING rewrite).
function isBuilding(t: Terrain): boolean {
  return t === Terrain.Wall || t === Terrain.HalfBuiltWall;
}

// Map a neighbor terrain through the "Boat / DeepSea -> River" rewrite
// that screenCalcRiver applies before comparing.
function asRiverContext(t: Terrain): Terrain {
  if (t === Terrain.DeepSea || t === Terrain.Boat) return Terrain.River;
  return t;
}

// Map "Boat -> River" rewrite that screenCalcDeepSea applies.
function asDeepSeaContext(t: Terrain): Terrain {
  return t === Terrain.Boat ? Terrain.River : t;
}

// ---- DeepSea --------------------------------------------------------

export function variantForDeepSea(n: Neighbors): Pos {
  const aL = asDeepSeaContext(n.aboveLeft);
  const a  = asDeepSeaContext(n.above);
  const aR = asDeepSeaContext(n.aboveRight);
  const l  = asDeepSeaContext(n.left);
  const r  = asDeepSeaContext(n.right);
  const bL = asDeepSeaContext(n.belowLeft);
  const b  = asDeepSeaContext(n.below);
  const bR = asDeepSeaContext(n.belowRight);
  const D = Terrain.DeepSea;
  const R = Terrain.River;

  if (aL !== D && a !== D && l !== D && r === D && b === D) return DEEP_SEA_VARIANTS.CORN1;
  if (aR !== D && a !== D && r !== D && l === D && b === D) return DEEP_SEA_VARIANTS.CORN2;
  if (bR !== D && b !== D && r !== D && l === D && a === D) return DEEP_SEA_VARIANTS.CORN4;
  if (bL !== D && b !== D && l !== D && r === D && a === D) return DEEP_SEA_VARIANTS.CORN3;
  if (l === R && r === D) return DEEP_SEA_VARIANTS.SIDE1;
  if (b === R && a === D) return DEEP_SEA_VARIANTS.SIDE2;
  if (a === R && b === D) return DEEP_SEA_VARIANTS.SIDE3;
  if (r === R && l === D) return DEEP_SEA_VARIANTS.SIDE4;
  return DEEP_SEA_VARIANTS.SOLID;
}

// ---- River ----------------------------------------------------------

export function variantForRiver(n: Neighbors): Pos {
  const a = asRiverContext(n.above);
  const b = asRiverContext(n.below);
  const l = asRiverContext(n.left);
  const r = asRiverContext(n.right);
  const isC = isRiverConn;

  // Surround: no neighbor in any cardinal direction connects.
  if (!isC(a) && !isC(b) && !isC(l) && !isC(r)) return RIVER_VARIANTS.SURROUND;

  // Ends: exactly one cardinal neighbor connects.
  if (!isC(a) && !isC(b) && isC(r) && !isC(l)) return RIVER_VARIANTS.END1;
  if (!isC(a) && !isC(b) && !isC(r) && isC(l)) return RIVER_VARIANTS.END2;
  if (!isC(a) && isC(b) && !isC(r) && !isC(l)) return RIVER_VARIANTS.END3;
  if (isC(a) && !isC(b) && !isC(r) && !isC(l)) return RIVER_VARIANTS.END4;

  // Corners: two adjacent cardinal sides don't connect.
  if (!isC(a) && !isC(l)) return RIVER_VARIANTS.CORN1;
  if (!isC(a) && !isC(r)) return RIVER_VARIANTS.CORN2;
  if (!isC(b) && !isC(l)) return RIVER_VARIANTS.CORN3;
  if (!isC(b) && !isC(r)) return RIVER_VARIANTS.CORN4;

  // Sides: two opposing cardinals don't connect. (Mirroring the source's
  // boolean expression, which has a redundant clause; we keep the order
  // because subsequent branches assume earlier ones did not match.)
  if (!isC(a) && !isC(b)) return RIVER_VARIANTS.SIDE1;
  if (!isC(l) && !isC(r)) return RIVER_VARIANTS.SIDE2;

  // OneSide: only one direction is non-connecting.
  if (!isC(l)) return RIVER_VARIANTS.ONESIDE1;
  if (!isC(b)) return RIVER_VARIANTS.ONESIDE2;
  if (!isC(r)) return RIVER_VARIANTS.ONESIDE3;
  if (!isC(a)) return RIVER_VARIANTS.ONESIDE4;

  return RIVER_VARIANTS.SOLID;
}

// ---- Forest ---------------------------------------------------------

// ---- Building -------------------------------------------------------

export function variantForBuilding(n: Neighbors): Pos {
  // Reduce to booleans up front to keep the cascade readable.
  const aL = isBuilding(n.aboveLeft);
  const a  = isBuilding(n.above);
  const aR = isBuilding(n.aboveRight);
  const l  = isBuilding(n.left);
  const r  = isBuilding(n.right);
  const bL = isBuilding(n.belowLeft);
  const b  = isBuilding(n.below);
  const bR = isBuilding(n.belowRight);

  if (aL && a && aR && l && r && bL && b && bR) return BUILD_VARIANTS.SOLID;
  if (r && a && b && l && !aR && !aL && !bR && !bL) return BUILD_VARIANTS.CROSS;
  if (r && a && b && l && !aR && !aL && !bR && bL) return BUILD_VARIANTS.MOST1;
  if (r && a && b && l && !aR && aL && !bR && !bL) return BUILD_VARIANTS.MOST2;
  if (r && a && b && l && !aR && !aL && bR && !bL) return BUILD_VARIANTS.MOST3;
  if (r && a && b && l && aR && !aL && !bR && !bL) return BUILD_VARIANTS.MOST4;

  if (aL && a && l && r && bL && b && bR) return BUILD_VARIANTS.SIDECORN13;
  if (a && aR && l && r && bL && b && bR) return BUILD_VARIANTS.SIDECORN14;
  if (aL && a && aR && l && r && bL && b) return BUILD_VARIANTS.SIDECORN15;
  if (aL && a && aR && l && r && b && bR) return BUILD_VARIANTS.SIDECORN16;

  if (l && r && a && b && aR && bL && !aL && !bR) return BUILD_VARIANTS.TWIST1;
  if (l && r && a && b && bR && aL && !aR && !bL) return BUILD_VARIANTS.TWIST2;

  if (a && l && r && b && bR && aR) return BUILD_VARIANTS.SIDECORN5;
  if (a && l && r && b && bL && aL) return BUILD_VARIANTS.SIDECORN6;
  if (a && l && r && b && bL && bR) return BUILD_VARIANTS.SIDECORN7;
  if (a && l && r && b && aL && aR) return BUILD_VARIANTS.SIDECORN8;
  if (a && b && l && !r && bL && !aL) return BUILD_VARIANTS.SIDECORN9;
  if (a && b && r && bR && !l && !aR) return BUILD_VARIANTS.SIDECORN10;
  if (a && b && r && aR && !bR) return BUILD_VARIANTS.SIDECORN11;
  if (a && b && l && aL && !bL) return BUILD_VARIANTS.SIDECORN12;

  if (r && a && l && !b && !aL && !aR) return BUILD_VARIANTS.T1;
  if (r && b && l && !bL && !bR) return BUILD_VARIANTS.T2;
  if (r && a && b && !aR && !bR) return BUILD_VARIANTS.T3;
  if (b && a && l && !aL && !bL) return BUILD_VARIANTS.T4;

  if (l && r && a && aR && !aL) return BUILD_VARIANTS.SIDECORN1;
  if (l && r && a && aL && !aR) return BUILD_VARIANTS.SIDECORN2;
  if (l && r && b && bL && !bR) return BUILD_VARIANTS.SIDECORN3;
  if (l && r && b && !a && bR && !bL) return BUILD_VARIANTS.SIDECORN4;

  if (r && a && b) return BUILD_VARIANTS.SIDE1;
  if (l && a && b) return BUILD_VARIANTS.SIDE2;
  if (r && l && b) return BUILD_VARIANTS.SIDE3;
  if (r && a && l) return BUILD_VARIANTS.SIDE4;

  if (r && b && bR) return BUILD_VARIANTS.CORNER1;
  if (l && b && bL) return BUILD_VARIANTS.CORNER2;
  if (r && a && aR) return BUILD_VARIANTS.CORNER3;
  if (l && a && aL) return BUILD_VARIANTS.CORNER4;

  if (r && b) return BUILD_VARIANTS.L1;
  if (l && b) return BUILD_VARIANTS.L2;
  if (r && a) return BUILD_VARIANTS.L3;
  if (l && a) return BUILD_VARIANTS.L4;
  if (l && r) return BUILD_VARIANTS.HORZ;
  if (a && b) return BUILD_VARIANTS.VERT;
  if (r) return BUILD_VARIANTS.HORZEND1;
  if (l) return BUILD_VARIANTS.HORZEND2;
  if (b) return BUILD_VARIANTS.VERTEND1;
  if (a) return BUILD_VARIANTS.VERTEND2;
  return BUILD_VARIANTS.SINGLE;
}

// ---- Road -----------------------------------------------------------

export function variantForRoad(n: Neighbors): Pos {
  // Source rewrites DEEP_SEA / BOAT to RIVER before checking neighbours.
  const aL = asRiverContext(n.aboveLeft);
  const a  = asRiverContext(n.above);
  const aR = asRiverContext(n.aboveRight);
  const l  = asRiverContext(n.left);
  const r  = asRiverContext(n.right);
  const bL = asRiverContext(n.belowLeft);
  const b  = asRiverContext(n.below);
  const bR = asRiverContext(n.belowRight);
  const R = Terrain.River;
  const Rd = Terrain.Road;

  if (aL !== Rd && a === Rd && aR !== Rd && l === Rd && r === Rd && bL !== Rd && b === Rd && bR !== Rd) return ROAD_VARIANTS.CROSS;
  if (a === Rd && l === Rd && r === Rd && b === Rd) return ROAD_VARIANTS.SOLID;

  // Road-water hybrids (river runs through / under / past the road).
  if (l === R && r === R && a === R && b === R) return ROAD_VARIANTS.WATER11;
  if (r === Rd && b === Rd && l === R && a === R) return ROAD_VARIANTS.WATER5;
  if (l === Rd && b === Rd && r === R && a === R) return ROAD_VARIANTS.WATER6;
  if (a === Rd && l === Rd && b === R && r === R) return ROAD_VARIANTS.WATER7;
  if (r === Rd && a === Rd && l === R && b === R) return ROAD_VARIANTS.WATER8;
  if (a === R && b === R) return ROAD_VARIANTS.WATER9;
  if (l === R && r === R) return ROAD_VARIANTS.WATER10;
  if (a === R && b === Rd) return ROAD_VARIANTS.WATER1;
  if (r === R && l === Rd) return ROAD_VARIANTS.WATER2;
  if (b === R && a === Rd) return ROAD_VARIANTS.WATER3;
  if (l === R && r === Rd) return ROAD_VARIANTS.WATER4;

  if (r === Rd && b === Rd && a === Rd && (aR === Rd || bR === Rd)) return ROAD_VARIANTS.SIDE1;
  if (l === Rd && r === Rd && b === Rd && (bL === Rd || bR === Rd)) return ROAD_VARIANTS.SIDE2;
  if (l === Rd && a === Rd && b === Rd && (bL === Rd || aL === Rd)) return ROAD_VARIANTS.SIDE3;
  if (l === Rd && r === Rd && a === Rd && (aR === Rd || aL === Rd)) return ROAD_VARIANTS.SIDE4;

  if (l === Rd && r === Rd && b === Rd) return ROAD_VARIANTS.T1;
  if (l === Rd && a === Rd && b === Rd) return ROAD_VARIANTS.T2;
  if (l === Rd && r === Rd && a === Rd) return ROAD_VARIANTS.T3;
  if (r === Rd && a === Rd && b === Rd) return ROAD_VARIANTS.T4;

  if (b === Rd && r === Rd && bR === Rd) return ROAD_VARIANTS.CORNER5;
  if (b === Rd && l === Rd && bL === Rd) return ROAD_VARIANTS.CORNER6;
  if (a === Rd && l === Rd && aL === Rd) return ROAD_VARIANTS.CORNER7;
  if (a === Rd && r === Rd && aR === Rd) return ROAD_VARIANTS.CORNER8;

  if (b === Rd && r === Rd) return ROAD_VARIANTS.CORNER1;
  if (b === Rd && l === Rd) return ROAD_VARIANTS.CORNER2;
  if (a === Rd && l === Rd) return ROAD_VARIANTS.CORNER3;
  if (a === Rd && r === Rd) return ROAD_VARIANTS.CORNER4;

  if (r === Rd || l === Rd) return ROAD_VARIANTS.HORZ;
  if (a === Rd || b === Rd) return ROAD_VARIANTS.VERT;

  return ROAD_VARIANTS.SOLID;
}

// ---- Boat -----------------------------------------------------------

// screenCalcBoat: rewrites DEEP_SEA + BOAT to RIVER for comparison,
// then picks one of 8 orientations based on which cardinal sides are
// non-river (i.e. shore). Mirrors the source exactly.
export function variantForBoat(n: Neighbors): Pos {
  const a = asRiverContext(n.above);
  const l = asRiverContext(n.left);
  const r = asRiverContext(n.right);
  const b = asRiverContext(n.below);
  const R = Terrain.River;
  if (a !== R && l !== R) return BOAT_ORIENTATION[5];
  if (a !== R && r !== R) return BOAT_ORIENTATION[6];
  if (b !== R && r !== R) return BOAT_ORIENTATION[7];
  if (b !== R && l !== R) return BOAT_ORIENTATION[4];
  if (l !== R) return BOAT_ORIENTATION[2];
  if (r !== R) return BOAT_ORIENTATION[3];
  if (b !== R) return BOAT_ORIENTATION[0];
  return BOAT_ORIENTATION[1];
}

// ---- Forest ---------------------------------------------------------

export function variantForForest(n: Neighbors): Pos {
  const a = n.above, b = n.below, l = n.left, r = n.right;

  if (!isForest(a) && !isForest(l) && isForest(r) && isForest(b)) return FOREST_VARIANTS.BR;
  if (!isForest(a) && isForest(l) && !isForest(r) && isForest(b)) return FOREST_VARIANTS.BL;
  if (isForest(a) && isForest(l) && !isForest(r) && !isForest(b)) return FOREST_VARIANTS.AL;
  if (isForest(a) && !isForest(l) && isForest(r) && !isForest(b)) return FOREST_VARIANTS.AR;
  if (isForest(a) && !isForest(l) && !isForest(r) && !isForest(b)) return FOREST_VARIANTS.ABOVE;
  if (!isForest(a) && !isForest(l) && !isForest(r) && isForest(b)) return FOREST_VARIANTS.BELOW;
  if (!isForest(a) && isForest(l) && !isForest(r) && !isForest(b)) return FOREST_VARIANTS.LEFT;
  if (!isForest(a) && !isForest(l) && isForest(r) && !isForest(b)) return FOREST_VARIANTS.RIGHT;
  if (!isForest(a) && !isForest(b) && !isForest(l) && !isForest(r)) return FOREST_VARIANTS.SINGLE;

  // Otherwise (interior of a forest cluster): the source falls through to
  // its initial `returnValue = FOREST` which we render as the SOLID-ish
  // grass-base FOREST tile (TERRAIN_TILE[Terrain.Forest]).
  return [48, 16];
}
